import React from 'react';

import classnames from 'classnames';

import { isSSR } from '@atlaskit/editor-common/is-ssr';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { GridType } from '@atlaskit/editor-common/types/grid';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import {
	type NamedPluginStatesFromInjectionAPI,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	akEditorBreakoutPadding,
	akEditorDefaultLayoutWidth,
	akEditorFullPageMaxWidth,
	breakoutWideScaleRatio,
} from '@atlaskit/editor-shared-styles/constants';

import type { GridPlugin } from './gridPluginType';
import type { CreateDisplayGrid, GridPluginOptions, GridPluginState, Highlights } from './types';

export const GRID_SIZE = 12;
// Matches the existing `.gridParent` 12px left + 12px right gutter expansion.
const GRID_GUTTER_WIDTH = 24;

const key = new PluginKey<GridPluginState>('gridPlugin');

const createDisplayGrid: CreateDisplayGrid = (view) => (props) => {
	const { dispatch, state } = view;

	const tr = state.tr.setMeta(key, props);

	dispatch(tr);

	return true;
};

type Side = 'left' | 'right';
const sides: Side[] = ['left', 'right'];

const overflowHighlight = (highlights: Highlights, side: Side, start: number, size?: number) => {
	if (!highlights.length) {
		return false;
	}

	const numericHighlights = highlights.filter(
		(highlight): highlight is number => typeof highlight === 'number',
	);
	const minHighlight = Math.min(...numericHighlights);
	const maxHighlight = Math.max(...numericHighlights);

	if (side === 'left') {
		return (
			minHighlight < 0 &&
			minHighlight <= -start &&
			(typeof size === 'number' ? minHighlight >= -(start + size) : true)
		);
	} else {
		return (
			maxHighlight > GRID_SIZE &&
			maxHighlight >= GRID_SIZE + start &&
			(typeof size === 'number' ? maxHighlight <= GRID_SIZE + size : true)
		);
	}
};

const gutterGridLines = (
	editorMaxWidth: number,
	editorWidth: number,
	highlights: Highlights,
	shouldCalcBreakoutGridLines?: boolean,
): JSX.Element[] => {
	const gridLines: JSX.Element[] = [];
	if (!shouldCalcBreakoutGridLines) {
		return gridLines;
	}

	const wideSpacing = (editorMaxWidth * breakoutWideScaleRatio - editorMaxWidth) / 2;
	sides.forEach((side) => {
		gridLines.push(
			<div
				key={side}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames(
					'gridLine',
					overflowHighlight(highlights, side, 0, 4) ? 'highlight' : '',
				)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ position: 'absolute', [side]: `-${wideSpacing}px` }}
			/>,
		);

		gridLines.push(
			<div
				key={side + '-bk'}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames('gridLine', highlights.indexOf('full-width') > -1 ? 'highlight' : '')}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					position: 'absolute',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					[side]: `-${(editorWidth - editorMaxWidth - akEditorBreakoutPadding) / 2}px`,
				}}
			/>,
		);
	});

	return gridLines;
};

const lineLengthGridLines = (highlights: Highlights) => {
	const gridLines: JSX.Element[] = [];
	const gridSpacing = 100 / GRID_SIZE;

	for (let i = 0; i <= GRID_SIZE; i++) {
		const style = {
			paddingLeft: `${gridSpacing}%`,
		};
		gridLines.push(
			<div
				key={i}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames('gridLine', highlights.indexOf(i) > -1 ? 'highlight' : '')}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={i < GRID_SIZE ? style : undefined}
			/>,
		);
	}

	return gridLines;
};

type Props = {
	containerElement: HTMLElement;
	editorWidth: number;
	gridType: GridType;
	highlight: Highlights;
	overlayWidth: number;

	shouldCalcBreakoutGridLines?: boolean;
	visible: boolean;
};

const GridNext = ({
	highlight,
	shouldCalcBreakoutGridLines,
	containerElement,
	editorWidth,
	gridType,
	overlayWidth,
	visible,
}: Props) => {
	const editorMaxWidth = akEditorDefaultLayoutWidth;
	const gridLines = [
		...lineLengthGridLines(highlight),
		...gutterGridLines(editorMaxWidth, editorWidth, highlight, shouldCalcBreakoutGridLines),
	];
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<div className="gridParent">
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={classnames('gridContainer', gridType)}
				style={{
					height: `${containerElement.scrollHeight}px`,
					display: visible ? 'block' : 'none',
					width: `${overlayWidth}px`,
				}}
				data-testid="gridContainer"
			>
				{gridLines}
			</div>
		</div>
	);
};

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<typeof gridPlugin>,
		'grid' | 'width'
	>,
) => {
	return {
		lineLength: states.widthState?.lineLength,
		width: states.widthState?.width,
		visible: states.gridState?.visible,
		gridType: states.gridState?.gridType,
		highlight: states.gridState?.highlight,
	};
};

interface ContentComponentProps {
	api: ExtractInjectionAPI<typeof gridPlugin> | undefined;
	editorView: EditorView;
	options: GridPluginOptions | undefined;
}

const ContentComponent = ({ api, editorView, options }: ContentComponentProps) => {
	const { lineLength, width, visible, gridType, highlight } = useSharedPluginStateWithSelector(
		api,
		['width', 'grid'],
		selector,
	);
	const overlayWidth = (lineLength || width || akEditorFullPageMaxWidth) + GRID_GUTTER_WIDTH;

	if (!visible || !highlight) {
		return null;
	}

	return (
		<GridNext
			shouldCalcBreakoutGridLines={options && options.shouldCalcBreakoutGridLines}
			editorWidth={width ?? akEditorFullPageMaxWidth}
			// Ignored via go/ees005
			// eslint-disable-next-line @atlaskit/editor/no-as-casting
			containerElement={editorView.dom as HTMLElement}
			visible={visible}
			gridType={gridType ?? 'full'}
			highlight={highlight}
			overlayWidth={overlayWidth}
		/>
	);
};

const EMPTY_STATE: GridPluginState = {
	visible: false,
	highlight: [],
};
const gridPMPlugin = new SafePlugin<GridPluginState>({
	key,
	state: {
		init() {
			return EMPTY_STATE;
		},
		apply(tr, currentPluginState) {
			const nextPluginState = tr.getMeta(key);
			if (nextPluginState) {
				return nextPluginState as GridPluginState;
			}

			return currentPluginState;
		},
	},
});

/**
 * Grid plugin to be added to an `EditorPresetBuilder` and used with `ComposableEditor`
 * from `@atlaskit/editor-core`.
 */
export const gridPlugin: GridPlugin = ({ config: options, api }) => {
	return {
		name: 'grid',

		getSharedState(editorState) {
			if (!editorState) {
				return null;
			}
			return key.getState(editorState) || null;
		},

		actions: {
			displayGrid: createDisplayGrid,
		},

		pmPlugins() {
			return [
				{
					name: 'grid',
					plugin: () => gridPMPlugin,
				},
			];
		},

		contentComponent: ({ editorView }) => {
			if (!editorView || isSSR()) {
				return null;
			}
			return <ContentComponent editorView={editorView} options={options} api={api} />;
		},
	};
};
